import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import ApexCharts from 'apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Empty } from 'antd';
import { isEqual } from 'lodash';

import { REAL_TIME_SCENARIO, selectors } from 'state';
import { formatters } from 'services';

type XY = { x: number; y: number };

const CHART_HEIGHT_PX = 400;

const FundsPerformance: React.FC = () => {
  const players = useSelector(selectors.activePlayers, isEqual);
  const { scenario_type, frequency } = useSelector(selectors.activeSession);

  const isRealtime = scenario_type === REAL_TIME_SCENARIO;

  // Stable chart id so ApexCharts.exec can target it
  const chartIdRef = useRef(
    `funds-performance-${Math.random().toString(36).slice(2)}`
  );
  const chartId = chartIdRef.current;

  // Build series data (all series included)
  const builtSeries = useMemo(() => {
    const raw = (players ?? []).map((player: any) => {
      const history = player.player_history ?? [];

      const data: XY[] = history.map((hs: any, i: number) => {
        const x = isRealtime ? new Date(hs.timestamp).getTime() : i;
        const y = (hs.portfolio_return ?? 0) * 100_000_000;
        return { x, y };
      });

      const lastY = data.length ? data[data.length - 1].y : 0;

      return {
        name: player.name as string,
        data,
        lastY,
      };
    });

    // Determine top 3 by last y (same logic as your Highcharts)
    const top3Names = new Set(
      [...raw]
        .sort((a, b) => (b.lastY ?? 0) - (a.lastY ?? 0))
        .slice(0, 3)
        .map(s => s.name)
    );

    return { raw, top3Names };
  }, [players, isRealtime]);

  const hasSeries = builtSeries.raw.length > 0;

  // Apex expects: [{ name, data }]
  const series = useMemo(() => {
    return builtSeries.raw.map(s => ({ name: s.name, data: s.data }));
  }, [builtSeries.raw]);

  const options = useMemo((): ApexOptions => {
    return {
      chart: {
        id: chartId,
        type: 'line',
        height: CHART_HEIGHT_PX,
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      title: { text: '' },
      legend: {
        show: true,
        position: 'right',
        horizontalAlign: 'center',
      },
      stroke: {
        curve: 'straight',
        width: 2,
      },
      markers: { size: 0 },
      xaxis: isRealtime
        ? { type: 'datetime' }
        : {
            type: 'numeric',
            title: { text: 'Period' },
            labels: {
              formatter: (val: string) => String(Math.trunc(Number(val))), // no decimals
            },
          },
      yaxis: {
        labels: {
          formatter: (v: number) => formatters.million(v),
        },
      },
      tooltip: isRealtime
        ? {
            x: {
              formatter: (val: number) => {
                // Highcharts: '%e %b %H:%M'
                const d = new Date(val);
                const day = d.getDate();
                const mon = d.toLocaleString('en-US', { month: 'short' });
                const hh = String(d.getHours()).padStart(2, '0');
                const mm = String(d.getMinutes()).padStart(2, '0');
                return `${day} ${mon} ${hh}:${mm}`;
              },
            },
            y: {
              formatter: (v: number) => formatters.commasNoDigits(v),
            },
          }
        : {
            x: {
              formatter: (val: number) => `Period: ${Math.trunc(val)}`,
            },
            y: {
              formatter: (v: number) => formatters.commasNoDigits(v),
              title: {
                formatter: (seriesName: string) => `${seriesName}: `,
              },
            },
          },
      grid: {
        padding: { left: 8, right: 8 },
      },
    };
  }, [chartId, isRealtime]);

  // ✅ Initial visibility: hide non-top3 after chart mounts / when series change
  useEffect(() => {
    if (!hasSeries) return;

    // First show everything (in case we re-rendered with a different set)
    for (const s of builtSeries.raw) {
      // showSeries/hideSeries are supported exec commands
      ApexCharts.exec(chartId, 'showSeries', s.name);
    }

    // Then hide all except top 3
    for (const s of builtSeries.raw) {
      if (!builtSeries.top3Names.has(s.name)) {
        ApexCharts.exec(chartId, 'hideSeries', s.name);
      }
    }
  }, [hasSeries, builtSeries.raw, builtSeries.top3Names, chartId]);

  // ✅ Early return AFTER hooks (no conditional hook calls)
  if (!hasSeries) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <Chart
      options={options}
      series={series as any}
      type="line"
      height={CHART_HEIGHT_PX}
    />
  );
};

export default FundsPerformance;
